// Hall of Fame — fonctions pures. Trois listes de la synthèse (functions/src/insert.js) : semaine, mois, tous les temps.
// L'affichage suit celui d'apps.0cog.com (zerocog/apps, public/leaderboard.js) : chaque liste montre ses meilleurs joueurs de
// la période, puis, grisés et datés, les meilleurs plus anciens qu'elle garde encore, pour rester pleine.
import { WINDOWS, insertScore, emptySummary } from '../../functions/src/insert.js';
import { avatarById, nameKey } from '../../functions/src/profile.js';
import { LEADERBOARD_DISPLAY_LIMIT } from '../config/constants.js';

// Ordre des onglets du Hall of Fame : la semaine d'abord
export const LIST_KEYS = Object.freeze(['week', 'month', 'all']);

/** Entrée valide (pseudo, score fini) normalisée, ou null : la synthèse et le stockage local sont des sources non fiables. */
function sanitizeEntry(e) {
  if (!e || typeof e !== 'object' || typeof e.nickname !== 'string' || !e.nickname.trim()) return null;
  const score = Number(e.score);
  if (!Number.isFinite(score)) return null;
  const entry = {
    id: String(e.id ?? ''),
    emoji: typeof e.emoji === 'string' ? e.emoji.slice(0, 8) : '',
    nickname: e.nickname.trim().slice(0, 24),
    score: Math.max(0, Math.floor(score)),
    date: typeof e.date === 'string' ? e.date : null,
  };
  if (typeof e.city === 'string' && e.city.trim()) entry.city = e.city.trim().slice(0, 30);
  if (typeof e.countryCode === 'string' && /^[A-Za-z]{2}$/.test(e.countryCode)) entry.countryCode = e.countryCode.toUpperCase();
  return entry;
}

/** Synthèse { updated, all, month, week } nettoyée, chaque liste triée par score décroissant. */
export function sanitizeSummary(raw) {
  const summary = emptySummary();
  if (!raw || typeof raw !== 'object') return summary;
  summary.updated = typeof raw.updated === 'string' ? raw.updated : null;
  for (const key of LIST_KEYS) {
    summary[key] = (Array.isArray(raw[key]) ? raw[key] : []).map(sanitizeEntry).filter(Boolean)
      .sort((a, b) => b.score - a.score);
  }
  return summary;
}

/**
 * Lignes d'une liste : ses `limit` meilleurs joueurs de la période (plus récents que `now - windowMs`), puis les meilleurs plus
 * anciens (`fill` : grisés, avec leur date). `rank` : la place affichée, médaille pour les trois premiers de la période.
 * @param {object[]} list triée par score décroissant
 * @param {number} windowMs Infinity pour « tous les temps »
 * @returns {{entry: object, rank: number, fill: boolean}[]}
 */
export function boardRows(list, windowMs, now, limit = LEADERBOARD_DISPLAY_LIMIT) {
  const inside = [];
  const older = [];
  for (const entry of list) {
    const time = entry.date ? new Date(entry.date).getTime() : NaN;
    (windowMs === Infinity || now - time <= windowMs ? inside : older).push(entry);
  }
  return [...inside.map((entry) => ({ entry, fill: false })), ...older.map((entry) => ({ entry, fill: true }))]
    .slice(0, limit)
    .map((row, i) => ({ ...row, rank: i + 1 }));
}

/**
 * Les lignes de chacune des trois listes, plus la ligne du joueur (`me`) s'il est dans la liste mais pas dans les premières.
 * @param {string|null} myId identifiant du joueur dans le Hall of Fame
 * @returns {Record<'week'|'month'|'all', {rows: object[], me: object|null}>}
 */
export function boards(summary, myId, now, limit = LEADERBOARD_DISPLAY_LIMIT) {
  return Object.fromEntries(LIST_KEYS.map((key) => {
    const all = boardRows(summary[key], WINDOWS[key], now, Infinity);
    const rows = all.slice(0, limit);
    const mine = myId ? all.find((row) => row.entry.id === myId) : null;
    return [key, { rows, me: mine && mine.rank > limit ? mine : null }];
  }));
}

/** Identifiant d'un profil dans le Hall of Fame tenu sur l'appareil (hors ligne, réglages modifiés). */
export const localId = (profile) => `local-${nameKey(profile.avatar, profile.pseudo)}`;

/** Inscrit un score dans le Hall of Fame tenu sur l'appareil, avec la même règle que la fonction submitScore. */
export function insertLocalScore(summary, profile, score, now) {
  const entry = {
    id: localId(profile),
    emoji: avatarById(profile.avatar)?.emoji ?? '',
    nickname: profile.pseudo,
    score,
    date: new Date(now).toISOString(),
  };
  if (profile.place?.city) entry.city = profile.place.city;
  if (profile.place?.countryCode) entry.countryCode = profile.place.countryCode;
  return sanitizeSummary(insertScore(summary, entry, now));
}

/** 'FR' → 🇫🇷 (lettres indicatrices régionales) ; rien pour un code invalide. */
export function flagOf(code) {
  if (typeof code !== 'string' || !/^[A-Za-z]{2}$/.test(code)) return '';
  return String.fromCodePoint(...[...code.toUpperCase()].map((c) => 0x1F1E6 + c.charCodeAt(0) - 65));
}
